import { IComponentOptions, IController, IPromise, IQService, IScope } from "angular";
import { Unsubscribe } from "redux";
import { IRestLayerRequest } from "@gtmhub/core";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import dayjs from "@gtmhub/dayjs";
import { EmployeeService, IEmployeeDetails } from "@gtmhub/employees";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { IMetricSelectEventDetail, OkrEvents, goalSelected } from "@gtmhub/okrs/events";
import { MetricService } from "@gtmhub/okrs/metrics/services/metric.service";
import { SearchService } from "@gtmhub/search/services/search.service";
import { createSessionTree } from "@gtmhub/sessions/controllers/utils";
import { PlanningSessionsActions } from "@gtmhub/sessions/redux/session-actions";
import { ISessionsStoreState } from "@gtmhub/sessions/redux/session-reducer";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { INgRedux } from "@gtmhub/state-management";
import { IReduxTeam, ITeam, ITeamsStoreState } from "@gtmhub/teams";
import { TeamsActions } from "@gtmhub/teams/redux/teams-actions";
import { getCurrentUserId } from "@gtmhub/users";
import { IdMap, toIdMap } from "@gtmhub/util";
import { Assignee } from "@webapp/assignees/models/assignee.models";
import { AttainmentTypeString } from "@webapp/okrs/goals/models/goal.models";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { ISearchCondition, ISearchField, SearchOperatorsEnum } from "@webapp/search/models/search-api.models";
import { Search, SearchDTO } from "@webapp/search/models/search.models";
import { IPlanningSessionsIdMap, PlanningSessionStatus, Session } from "@webapp/sessions/models/sessions.model";
import { IGoal, IGoalNode, SingleGoalSelectorInvokedBy } from "../../models";
import { GoalService } from "../../services/goal.service";
import { GoalsTreeService } from "../../services/goals-tree.service";

export interface IItemSelectorOptions {
  excludeSessionId?: string;
  planningSessionId?: string;
  showFutureSessions?: boolean;
  showArchivedSessions?: boolean;
  enableOkrSelectionOnly?: boolean;
  unselectableId?: string;
  invokedById?: string;
  invokedBy?: SingleGoalSelectorInvokedBy;
  includeMetrics?: boolean;
}

type SearchBy = "goals" | "metrics";

const metricFields = "id,name,ownerIds,cascadeType,goalId,goalName,goalIds,sessionId,obfuscated";

export interface ISelectedMetric {
  id: string;
  name: string;
  ownerIds: string[];
  cascadeType: string;
  goalId: string;
  goalName: string;
  sessionId: string;
  obfuscated?: boolean;
}

const goalFields = "parentId,assignee,description,name,sessionId,obfuscated,private,parentGoalSessionId,ownerIds,attainmentTypeString";

export interface ISelectedGoal {
  id: string;
  parentId?: string;
  assignee?: Assignee;
  description?: string;
  name: string;
  sessionId: string;
  obfuscated?: boolean;
  private?: boolean;
  parentGoalSessionId?: string;
  ownerIds?: string[];
  attainmentTypeString: AttainmentTypeString;
}

export type ISelection = { type: "goal"; goal: ISelectedGoal } | { type: "metric"; metric: ISelectedMetric } | { type: "session"; session: Session };
export interface IGoalSelectorMyItems {
  myGoals: IGoal[];
  myTeamsGoals: IGoal[];
  myParentTeamsGoals: IGoal[];
  myMetrics: Metric[];
  myTeamsMetrics: Metric[];
  myParentTeamsMetrics: Metric[];
}

const includeMetricsInvokedBy: SingleGoalSelectorInvokedBy[] = ["TaskRow", "TaskSingleView", "TaskKanban"];

export interface ISingleGoalSelectorComponentBindings {
  excludeSessionId?: string;
  planningSessionId?: string;
  unselectableId?: string;
  invokedById?: string;
  invokedBy?: SingleGoalSelectorInvokedBy;
  includeMetrics?: boolean;
  showFutureSessions?: boolean;
  showArchivedSessions?: boolean;
  enableOkrSelectionOnly?: boolean;
  cancel?(): void;
  select?({ selection }): (selection: ISelection) => void;
}

interface ISingleGoalSelectorComponentRedux {
  sessionsTree: { children: [] };
  sessionsMap: IPlanningSessionsIdMap;
  teamsIdMap: IdMap<IReduxTeam>;
  reduxItemsFetched: boolean;
  reduxItemsError: IUIError;
}

export interface SingleGoalSelectorCtrl extends ISingleGoalSelectorComponentRedux, ISingleGoalSelectorComponentBindings {}

export class SingleGoalSelectorCtrl implements IController {
  selected: {
    screen: "sessions" | "goals";
  };
  indicators: {
    gettingGoals: boolean;
    loading: boolean;
  };
  goals: IGoalNode;
  metrics: Partial<Metric>[];
  selectedSession: Session;
  goalFilterQuery: string;
  myItems: IGoalSelectorMyItems;
  unsubscribeFromStore: Unsubscribe;
  searchBy: SearchBy;

  private ownerInfo: {
    id: string;
    teamIds: string[];
    parentTeamIds: string[];
  };
  private isAllMetricsLoaded: boolean;
  private unsubscribeFromWatchGroupForLoadAllMetrics: () => void;
  private okrEvents: OkrEvents;

  public static $inject = [
    "$scope",
    "$q",
    "TeamsActions",
    "GoalService",
    "GoalsTreeService",
    "UIErrorHandlingService",
    "$ngRedux",
    "PlanningSessionsActions",
    "EmployeeService",
    "MetricService",
    "$rootScope",
    "SearchService",
  ];

  constructor(
    private $scope: IScope & IExtendedRootScopeService,
    private $q: IQService,
    private teamsActions: TeamsActions,
    private goalService: GoalService,
    private goalsTreeService: GoalsTreeService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $ngRedux: INgRedux,
    private planningSessionsActions: PlanningSessionsActions,
    private employeeService: EmployeeService,
    private metricService: MetricService,
    private $rootScope: ITraceRootScopeService,
    private searchService: SearchService
  ) {
    this.okrEvents = new OkrEvents(this.$scope);

    this.sessionsMap = {};
    this.myItems = {
      myGoals: [],
      myTeamsGoals: [],
      myParentTeamsGoals: [],
      myMetrics: [],
      myTeamsMetrics: [],
      myParentTeamsMetrics: [],
    };
    this.ownerInfo = {
      id: getCurrentUserId(),
      teamIds: [],
      parentTeamIds: [],
    };
    this.selected = {
      screen: "sessions",
    };
    this.searchBy = "goals";
    this.indicators = {
      gettingGoals: false,
      loading: true,
    };
    this.goalFilterQuery = "";
  }

  get hasSessions(): boolean {
    return this.sessionsMap && Object.keys(this.sessionsMap).length > 0;
  }

  private get sessionsIds(): string[] {
    return this.sessionsMap ? Object.keys(this.sessionsMap) : [];
  }

  $onInit(): void {
    this.okrEvents = new OkrEvents(this.$scope);

    this.$rootScope.traceAction("single_goal_selector_init", () => {
      this.unsubscribeFromWatchGroupForLoadAllMetrics = this.$scope.$watchGroup(["$ctrl.selected.screen", "$ctrl.searchBy"], () => {
        if (this.selected.screen === "sessions" && this.searchBy === "metrics" && !this.isAllMetricsLoaded) {
          this.loadAllMetrics();
          this.unsubscribeFromWatchGroupForLoadAllMetrics();
        }
      });

      this.unsubscribeFromStore = this.$ngRedux.connect(this.bindStateToThis.bind(this))(this);
      this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());
      this.$ngRedux.dispatch(this.teamsActions.fetchTeamsIfMissing());

      this.$scope
        .watchUntilAndRejectOnError(
          () => this.reduxItemsFetched,
          () => this.reduxItemsError
        )
        .then(
          () => this.loadData(),
          () => this.uiErrorHandlingService.handleModal(this.reduxItemsError)
        );
    });
  }

  $onDestroy(): void {
    this.unsubscribeFromStore();
    this.unsubscribeFromWatchGroupForLoadAllMetrics();
  }

  selectSessionFromList(session: { data: Session }): void {
    this.$rootScope.traceAction("select_session_from_list", () => {
      this.selected.screen = "goals";
      this.selectedSession = session.data;
      this.goalFilterQuery = "";

      if (this.searchBy === "metrics") {
        this.loadMetrics([session.data.id]);
      } else {
        this.loadGoals([session.data.id]);
      }
    });
  }

  backToSessions(): void {
    this.$rootScope.traceAction("back_to_sessions", () => {
      this.changeView("sessions");
      this.goals = {
        goal: {},
        children: [],
      };
      this.selectedSession = {};
      this.goalFilterQuery = "";
    });
  }

  filterByName(goalFilterQuery: string): void {
    this.$rootScope.traceAction("filter_by_name", () => {
      if (this.selected.screen === "sessions") {
        this.selectedSession = {};
      }

      if (!goalFilterQuery?.length && !this.selectedSession.id) {
        this.changeView("sessions");
      } else {
        this.changeView("goals");

        const sessionIds = this.selectedSession.id ? [this.selectedSession.id] : this.sessionsIds;

        if (this.searchBy === "metrics") {
          this.loadMetrics(sessionIds, goalFilterQuery);
        } else {
          this.loadGoals(sessionIds, goalFilterQuery);
        }
      }
    });
  }

  selectSession(session: Session): void {
    this.$rootScope.traceAction("select_session", () => {
      this.$scope.$root.$broadcast("sessionSelected", session);
      this.select({ selection: { type: "session", session } });
    });
  }

  selectGoal(goal: IGoal): void {
    this.$rootScope.traceAction("select_goal", () => {
      this.okrEvents.broadcastGoalsChanged(goalSelected, { goal, invokedById: this.invokedById, invokedBy: this.invokedBy });
      this.selectGoalNoBroadcast(goal);
    });
  }

  selectGoalNoBroadcast(goal: IGoal): void {
    this.$rootScope.traceAction("select_goal_no_broadcast", () => {
      this.select({ selection: { type: "goal", goal } });
    });
  }

  selectMetric(metric: Metric): void {
    this.$rootScope.traceAction("select_metric", () => {
      const eventDetail: IMetricSelectEventDetail = {
        metric,
        invokedById: this.invokedById,
        invokedBy: this.invokedBy,
      };
      if (this.goals && this.goals.goal) {
        eventDetail.parent = this.goals.goal;
      }
      this.okrEvents.broadcastGoalsChanged("metricSelected", eventDetail);
      this.select({ selection: { type: "metric", metric } });
    });
  }

  toggleSearchBy(searchBy: SearchBy): void {
    if (this.searchBy === searchBy) {
      return;
    }

    this.$rootScope.traceAction("toggle_search_by", () => {
      this.searchBy = searchBy;

      const searchIsPerformed = !!this.goalFilterQuery;
      // when a session is selected and nothing is enetered in search field we still need to load other collection if toggling searchBy
      const goalsFromSelectedSessionAreDisplayed = this.selected.screen === "goals" && !!this.selectedSession.id;

      if (searchIsPerformed || goalsFromSelectedSessionAreDisplayed) {
        const sessionIds = searchIsPerformed ? this.sessionsIds : [this.selectedSession.id];
        switch (this.searchBy) {
          case "goals":
            this.loadGoals(sessionIds, this.goalFilterQuery);
            break;
          case "metrics":
            this.loadMetrics(sessionIds, this.goalFilterQuery);
            break;
        }
      }
    });
  }

  private bindStateToThis(state: ISessionsStoreState & ITeamsStoreState) {
    const isActionAllowedForThisSession = (session: Session) =>
      (<SingleGoalSelectorInvokedBy[]>["CloneOKR", "KR"]).includes(this.invokedBy)
        ? session.status === PlanningSessionStatus.OPEN
        : session.status !== PlanningSessionStatus.ARCHIVED;

    const allowedSessions = state.sessions.items.filter((s) => (isActionAllowedForThisSession(s) || this.showArchivedSessions) && s.id !== this.excludeSessionId);
    let sessions = this.showFutureSessions ? allowedSessions : allowedSessions.filter((s) => dayjs().isSameOrBefore(s.end));

    if (this.planningSessionId) {
      const allowedSessionMap = toIdMap(allowedSessions);
      const planningSession = allowedSessionMap[this.planningSessionId];
      if (planningSession) {
        const parentSession = allowedSessionMap[planningSession.parentId];

        if (parentSession) {
          sessions = [parentSession, planningSession];
        } else {
          sessions = [planningSession];
        }
      }
    }

    return {
      sessions,
      sessionsTree: this.buildSessionsTree(sessions),
      sessionsMap: toIdMap(sessions),
      teamsIdMap: state.teams.map,
      reduxItemsFetched: state.teams.isFetched && state.sessions.isFetched,
      reduxItemsError: state.teams.error || state.sessions.error,
    };
  }

  private loadData(): IPromise<boolean | void> {
    return this.employeeService
      .getEmployee(this.ownerInfo.id)
      .then((employee) => {
        this.ownerInfo.teamIds = this.getMyTeamsIds(employee);
        const initPromises: IPromise<unknown>[] = [];

        initPromises.push(this.getMyGoals());

        if (includeMetricsInvokedBy.includes(this.invokedBy)) {
          this.includeMetrics = true;
        }

        if (!this.includeMetrics) {
          this.unsubscribeFromWatchGroupForLoadAllMetrics();
        }

        if (this.ownerInfo.teamIds.length) {
          initPromises.push(this.getMyTeamGoals());
        }

        return this.$q.all(initPromises).then(() => (this.indicators.loading = false));
      })
      .catch((error) => {
        this.indicators.loading = false;
        this.uiErrorHandlingService.handleModal(error);
      });
  }

  private loadGoals(sessionIds: string[], filterQuery = ""): void {
    this.indicators.gettingGoals = true;
    const searchBody = this.getSearchBody(sessionIds, filterQuery, "goals");
    this.searchService.getSomeSearchData<Search<"goals">>(searchBody).then(
      (result) => {
        const goals = result.items.map(this.formatGoalsSearchResponse);

        if (result.items.length) {
          this.goals = this.goalsTreeService.constructGoalsTree(result.items[0].fields.sessionId, goals, [{ fieldName: "name", reversed: false }]);
        } else {
          this.goals = {
            goal: {},
            children: [],
          };
        }

        this.indicators.gettingGoals = false;
      },
      (err) => {
        this.indicators.gettingGoals = false;
        this.uiErrorHandlingService.handleModal(err);
      }
    );
  }

  private formatGoalsSearchResponse(item: Search<"goals">): IGoal {
    const parentId = item.fields.parent?.goalId || item.fields.parent?.metricId;
    // Obfuscated is not part of the search api response, it’s something v2 api calculates.
    // The search api returns private goals/metrics that the user making the request owns, that’s why it’s set explicitly to false here
    return { id: item.id, parentId: parentId, obfuscated: false, ...item.fields };
  }

  private loadMetrics(sessionIds: string[], filterQuery = ""): void {
    this.indicators.gettingGoals = true;
    const searchBody = this.getSearchBody(sessionIds, filterQuery, "metrics");
    this.searchService.getSomeSearchData<Search<"metrics">>(searchBody).then(
      (result) => {
        this.metrics = result.items.map((i: Search<"metrics">) => {
          // Obfuscated is not part of the search api response, it’s something v2 api calculates.
          // The search api returns private goals/metrics that the user making the request owns, that’s why it’s set explicitly to false here
          return { id: i.id, obfuscated: false, ...i.fields };
        });
        this.indicators.gettingGoals = false;
      },
      (err) => {
        this.indicators.gettingGoals = false;
        this.uiErrorHandlingService.handleModal(err);
      }
    );
  }

  private generateMongoParamsOnInit(sessionIds: string[], ownerIds: string[], fields: string): IRestLayerRequest {
    return {
      filter: {
        $and: [
          { sessionId: { $in: sessionIds } },
          { ownerIds: { $in: ownerIds } },
          ...(this.invokedBy === "KR" ? [{ attainmentType: { $ne: "average_children" } }] : []),
        ],
      },
      fields,
    };
  }

  private getSearchBody(sessionIds: string[], filterQuery: string, collectionName: string): SearchDTO {
    const searchConditions: ISearchCondition[] = [
      {
        fieldName: collectionName === "goals" ? "sessionId" : "session._id",
        fieldCondition: {
          operator: "in",
          value: sessionIds,
        },
      },
    ];

    if (this.invokedBy === "KR") {
      searchConditions.push({
        fieldName: "attainmentType",
        fieldCondition: {
          operator: "isNot",
          value: "average_children",
        },
      });
    }

    if (this.unselectableId && collectionName === "metrics") {
      searchConditions.push({
        fieldName: "goalId",
        fieldCondition: {
          operator: "isNot",
          value: this.unselectableId,
        },
      });
    }

    const searchFields: ISearchField[] = filterQuery
      ? [
          {
            name: "name",
          },
          {
            name: "description",
          },
          {
            name: "assignees.name",
          },
        ]
      : [];

    return {
      searchTerm: filterQuery,
      operator: filterQuery ? SearchOperatorsEnum.matchPrefix : SearchOperatorsEnum.matchAny,
      searchRequests: [
        {
          collectionName: collectionName,
          searchFields: searchFields,
          searchConditions: searchConditions,
        },
      ],
    };
  }

  private buildSessionsTree(sessions: Session[]) {
    if (!sessions || sessions.length === 0) {
      return null;
    }

    return createSessionTree(sessions).sort((a: { data: Session }, b: { data: Session }) => {
      const aEndDate = dayjs(a.data.end);
      const bEndDate = dayjs(b.data.end);

      // sorted by session end date desc
      if (aEndDate.isAfter(bEndDate)) {
        return 1;
      } else if (aEndDate.isBefore(bEndDate)) {
        return -1;
      }
      return 0;
    });
  }

  private getMyGoals(): IPromise<unknown> {
    const mongoGetMyGoalsParams = this.generateMongoParamsOnInit(this.sessionsIds, [this.ownerInfo.id], goalFields);

    return this.goalService.getGoalsV2(mongoGetMyGoalsParams).then(({ items }) => (this.myItems.myGoals = items));
  }

  private getMyTeamGoals(): IPromise<unknown> {
    const myTeamGoalsPromises: IPromise<unknown>[] = [];
    const teams = (this.ownerInfo?.teamIds || []).map((teamId) => this.teamsIdMap[teamId]);

    this.ownerInfo.parentTeamIds = this.getParentTeamsIds(teams);
    if (this.ownerInfo.parentTeamIds.length) {
      myTeamGoalsPromises.push(this.getMyParentTeamGoals());
    }

    const mongoGetMyTeamGoalsParams = this.generateMongoParamsOnInit(this.sessionsIds, this.ownerInfo.teamIds, goalFields);

    myTeamGoalsPromises.push(this.goalService.getGoalsV2(mongoGetMyTeamGoalsParams).then(({ items }) => (this.myItems.myTeamsGoals = items)));

    return this.$q.all(myTeamGoalsPromises);
  }

  private getMyParentTeamGoals(): IPromise<unknown> {
    const mongoGetMyParentTeamsGoalsParams = this.generateMongoParamsOnInit(this.sessionsIds, this.ownerInfo.parentTeamIds, goalFields);

    return this.goalService.getGoalsV2(mongoGetMyParentTeamsGoalsParams).then(({ items }) => (this.myItems.myParentTeamsGoals = items));
  }

  private loadAllMetrics() {
    this.indicators.loading = true;

    const mongoGetMyMetricsParams = this.generateMongoParamsOnInit(this.sessionsIds, [this.ownerInfo.id], metricFields);
    const metricsMapPromises = {
      myMetrics: this.metricService.getMetricsV2(mongoGetMyMetricsParams),
      myTeamsMetrics: null,
      myParentTeamsMetrics: null,
    };

    if (this.ownerInfo.teamIds.length) {
      const mongoGetMyTeamsMetricsParams = this.generateMongoParamsOnInit(this.sessionsIds, this.ownerInfo.teamIds, metricFields);
      metricsMapPromises.myTeamsMetrics = this.metricService.getMetricsV2(mongoGetMyTeamsMetricsParams);
    }

    if (this.ownerInfo.parentTeamIds.length) {
      const mongoGetMyParentTeamsMetricsParams = this.generateMongoParamsOnInit(this.sessionsIds, this.ownerInfo.parentTeamIds, metricFields);
      metricsMapPromises.myParentTeamsMetrics = this.metricService.getMetricsV2(mongoGetMyParentTeamsMetricsParams);
    }

    this.$q.all(metricsMapPromises).then(
      (resolvedMetricsMap) => {
        this.myItems.myMetrics = resolvedMetricsMap.myMetrics.items;

        if (resolvedMetricsMap.myTeamsMetrics) {
          this.myItems.myTeamsMetrics = resolvedMetricsMap.myTeamsMetrics.items;
        }
        if (resolvedMetricsMap.myParentTeamsMetrics) {
          this.myItems.myParentTeamsMetrics = resolvedMetricsMap.myParentTeamsMetrics.items;
        }

        this.isAllMetricsLoaded = true;
        this.indicators.loading = false;
      },
      (error) => {
        this.indicators.loading = false;
        this.uiErrorHandlingService.handleModal(error);
      }
    );
  }

  private changeView(selectedView: "sessions" | "goals") {
    this.selected.screen = selectedView;
  }

  private getMyTeamsIds(employee: IEmployeeDetails): string[] {
    return employee.info.managedTeamIds.concat(employee.info.teamIds).filter((teamId, index, self) => self.indexOf(teamId) === index);
  }

  private getParentTeamsIds(teams: ITeam[]): string[] {
    return (teams || []).map((team) => team?.parentId).filter((teamId, index, self) => teamId && self.indexOf(teamId) === index);
  }
}

export const SingleGoalSelectorComponent: IComponentOptions = {
  template: require("./single-goal-selector.html"),
  controller: SingleGoalSelectorCtrl,
  bindings: {
    excludeSessionId: "@",
    planningSessionId: "@",
    unselectableId: "@",
    invokedById: "@",
    invokedBy: "@",
    includeMetrics: "<",
    showFutureSessions: "<",
    showArchivedSessions: "<",
    enableOkrSelectionOnly: "<",
    cancel: "&",
    select: "&",
  },
};
